import { createTheme } from "@mui/material";

//Colours that are part of the product's look but do not map onto one of MUI's
//standard palette slots. Anything a page needs beyond primary/text/error belongs
//here rather than as a literal in an sx prop, so a change to the look is one
//edit in this file.
//
//Third-party brand marks are deliberately not here: the Google, Microsoft and
//Slack colours in the icon components are those companies' colours, not ours,
//and must not follow a theme change.
declare module "@mui/material/styles" {
    interface Palette {
        custom: CustomPalette
    }
    interface PaletteOptions {
        custom?: CustomPalette
    }
}

interface CustomPalette {
    //muted greys out an icon or a placeholder inside a table
    muted: string
    //highlight is the pale blue behind a hovered, selected or dragged row
    highlight: string
    //accent is the stronger blue of the column resizer and the sort arrow
    accent: string
    //border is the hairline around the sidebar and table headers
    border: string
    //borderStrong outlines a card, borderMuted the activity filter tabs,
    //borderInput the query box. Four border greys is drift the design should
    //settle; they are kept apart here so that naming them changes nothing on
    //screen
    borderStrong: string
    borderMuted: string
    borderInput: string
    //surfaceHover is the grey behind a hovered sidebar item
    surfaceHover: string
    //surfaceSection is the tinted band behind a section header inside a grouped
    //dropdown, where the header names a different kind of thing than the rows
    //under it - a directory, not one of its groups. It is deliberately darker
    //and cooler than surfaceHover so a header never reads as a hovered row
    surfaceSection: string
    //link is an inline link inside a configuration label
    link: string
    //status* colour the notification toasts. They are intentionally separate
    //from MUI's error/success/warning slots, which 47 components already render
    //with; unifying the two is a design decision, not a refactor
    statusError: string
    statusSuccess: string
    statusWarning: string
    //shield* outline the Nullafi Shield banner on the configuration page
    shieldEnabled: string
    shieldDisabled: string
    shieldDisabledIcon: string
}

const palette = createTheme({
    palette: {
        primary: {
            main: "#2158c3",
        },
        text: {
            primary: "#152662",
            secondary: "#4B5C97",
        },
        custom: {
            muted: "#B0B7CF",
            highlight: "#E2F2FF",
            accent: "#3A75E8",
            border: "#DDDDDD",
            borderStrong: "#CCD0E0",
            borderMuted: "#CCCCCC",
            borderInput: "#AAAAAA",
            surfaceHover: "#F6F6F6",
            surfaceSection: "#EAEDF5",
            link: "#0000EE",
            statusError: "#EB417E",
            statusSuccess: "#09D17D",
            statusWarning: "#FF9900",
            shieldEnabled: "#95CCFC",
            shieldDisabled: "#F77ABD",
            shieldDisabledIcon: "#D82666",
        },
    },
    typography: {
        fontFamily: "Inter Variable",
        h6: {
            fontWeight: 600,
            fontSize: "14px",
            lineHeight: "20px",
        },
        h5: {
            fontWeight: 600,
            fontSize: "14px",
            lineHeight: "20px",
        },
        h4: {
            fontWeight: 600,
            fontSize: "16px",
            lineHeight: "24px",
        },
        h3: {
            fontWeight: 600,
            fontSize: "18px",
            lineHeight: "24px",
        },
        h2: {
            fontWeight: 700,
            fontSize: "24px",
            lineHeight: "30px",
        },
        h1: {
            fontWeight: 700,
            fontSize: "30px",
            lineHeight: "40px",
        },
        subtitle1: {
            fontWeight: 400,
            fontSize: "14px",
            lineHeight: "20px",
        },
        subtitle2: {
            fontWeight: 400,
            fontSize: "12px",
            lineHeight: "16px",
        },
        body1: {
            fontWeight: 400,
            fontSize: "14px",
            lineHeight: "20px",
        },
        body2: {
            fontWeight: 400,
            fontSize: "12px",
            lineHeight: "16px",
        },
        button: {
            fontWeight: 400,
            fontSize: "14px",
            lineHeight: "20px",
            textTransform: "none"
        },
        caption: {
            fontWeight: 400,
            fontSize: "12px",
            lineHeight: "16px",
        },
    },
});

//A second pass so the component overrides can read the palette above instead
//of repeating its values.
export const mdTheme = createTheme(palette, {
    components: {
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
                    borderRadius: "6px"
                }
            }
        },
        MuiChip: {
            styleOverrides: {
                outlined: {
                    boxSizing: "border-box",
                    borderRadius: "6px",
                },
                sizeSmall: {
                    borderRadius: "4px",
                    fontSize: "12px"
                },
                deleteIcon: {
                    fill: palette.palette.custom.accent,
                }
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    border: `0.5px solid ${palette.palette.custom.borderStrong}`,
                    boxSizing: "border-box",
                    borderRadius: "10px",
                    boxShadow: "none",
                    padding: "40px",
                },
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    color: palette.palette.text.secondary,
                    "&:hover": {
                        backgroundColor: palette.palette.custom.surfaceHover,
                    },
                    "&.Mui-selected": {
                        color: palette.palette.primary.main,
                        backgroundColor: "transparent",
                    },
                },
            },
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    color: "inherit",
                },
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    "&.Mui-selected": {
                        color: palette.palette.primary.contrastText,
                        backgroundColor: palette.palette.primary.main,
                        "&:hover": {
                            backgroundColor: palette.palette.primary.dark,
                        },
                    },
                },
            },
        },
        MuiTable: {
            styleOverrides: {
                root: {
                    overflow: "auto",
                    tableLayout: "fixed"
                }
            }
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    "&:nth-of-type(even)": {
                        backgroundColor: "rgba(241, 243, 252, 0.5)"
                    },
                    "&:hover > td": {
                        backgroundColor: palette.palette.custom.highlight
                    }
                },
            }
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderBottom: "none",

                }
            }
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    borderBottom: `${palette.palette.custom.border} solid 1px`,
                    "& .column-resizer": {
                        transition: "opacity 0.3s",
                        cursor: "ew-resize",
                        opacity: 0,
                        userSelect: "none",
                        background: palette.palette.custom.accent,
                        position: "absolute",
                        top: 0,
                        bottom: 0,
                        right: 0,
                        margin: "auto",
                        width: "3px",
                        height: "40px"
                    },
                    "& th:hover": {
                        backgroundColor: palette.palette.custom.highlight,
                        "& .column-resizer": {
                            opacity: 1
                        }
                    }
                },
            }
        },
    },

});

export default mdTheme;
